import { useInfiniteQuery } from '@tanstack/react-query';

import { client } from '@/api/common';
import type { Reservation } from '@/api/reservations';

type Variables = {};
type Options = {
  enabled?: boolean;
};

export type CombinedHomeFeedReservation = Reservation & {
  feedType: 'upcoming' | 'to_review';
  eventTime: string;
};

type ReservationsPage = {
  data: CombinedHomeFeedReservation[];
  meta: {
    page: number;
    totalPages: number;
    totalCount?: number;
  };
};

const areRequiredParamsDefined = (
  variables: Variables,
  requiredKeys: (keyof Variables)[]
) => requiredKeys.every((key) => variables[key] !== '');

const fetchCombinedReservations = async ({
  pageParam,
}: {
  pageParam: number;
  variables: Variables;
}) => {
  try {
    const params: Record<string, any> = {
      page: pageParam,
      per_page: 5,
    };

    const response = await client.get(
      'v2/internal/partners/reservations/combined-home-feed',
      {
        params,
      }
    );

    return response.data;
  } catch (error) {
    console.error('fetchCombinedReservations error:', error);
    throw error;
  }
};

export const useCombinedHomeFeedReservations = (
  variables: Variables,
  options?: Options
) => {
  const requiredKeys: (keyof Variables)[] = [];
  const shouldFetch = areRequiredParamsDefined(variables, requiredKeys);

  return useInfiniteQuery({
    queryKey: ['internal/partners/reservations/combined-home-feed'],
    queryFn: ({ pageParam = 1 }) =>
      fetchCombinedReservations({
        pageParam,
        variables,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ReservationsPage) => {
      if (lastPage.meta.page >= lastPage.meta.totalPages) {
        return undefined;
      }
      return lastPage.meta.page + 1;
    },
    enabled: shouldFetch && (options?.enabled ?? true),
  });
};
